import React from 'react'
import L from 'leaflet'

// Garantir que L esteja disponível globalmente para o plugin ANTES de importar
if (typeof window !== 'undefined') {
  window.L = L
}

import 'leaflet-draw'
import 'leaflet.icon.glyph'
import 'leaflet/dist/leaflet.css'
import 'leaflet-draw/dist/leaflet.draw.css'
import 'leaflet/src/control'

// Importar o plugin do Google Maps usando o módulo ES6
// O módulo ES6 exporta a classe diretamente, então precisamos registrá-la manualmente
import GoogleMutant from 'leaflet.gridlayer.googlemutant/src/Leaflet.GoogleMutant.mjs'

// Registrar o plugin no Leaflet
if (GoogleMutant && L && L.GridLayer) {
  L.GridLayer.GoogleMutant = GoogleMutant
  // Criar a factory function
  if (L.gridLayer && !L.gridLayer.googleMutant) {
    L.gridLayer.googleMutant = function(options) {
      return new GoogleMutant(options)
    }
  }
}
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'
import 'leaflet.markercluster/dist/leaflet.markercluster.js'
import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import icon from 'leaflet/dist/images/marker-icon.png'
import { withSnackbar } from 'notistack'

import AreaMenu from '@images/map/draw_area.svg'
import Alert from '@images/alerts/alert.svg'
import locationPickerIcone from '@images/leaflet/picker_default.svg'
import locationPickerActiveIcone from '@images/leaflet/picker_selected.svg'
import WeatherForecast from '@images/equipment/weather_forecast.svg'

import CustomMenu from './Controls'
import { withStyles } from '@material-ui/core'
import { areNotEqual } from '@models/area'
import {
  createCustomControl,
  changeCustomControl,
  locationPickerPopupContent
} from '@models/leaflet'
import {
  circleToPolygon,
  changeMapStyle,
  changeAreaMenuStyle,
  getLeafLetTranslations,
  getZoom,
  getImageBounds
} from '@models/map'

import { getEquipmentIcon } from '@models/equipment/equipment'

import { IntercomAPI } from '@utils/IntercomAPI'
import { sendAmplitudeEvent } from '@utils/amplitudeEvent';

import { withMenusStatus, MENUS } from '@contexts/MenusStatusProvider'

import ProductsFloatingMenu from './productsFloatingMenu/Index'
import styles from './styles'

const defaultAreaColor = '#00F0FF'
class Map extends React.Component {
  constructor(props) {
    super(props)

    this.forecastProductReference = React.createRef()

    this.state = {
      isActivePicker: false,
      drawing: false,
      drawItems: new L.FeatureGroup(),
      clusterLayer: null,
      markersLayer: new L.FeatureGroup(),
      filter: null,
      currentAreaWithFilter: undefined,
      scale: null,
      geocoderLayer: undefined,
      setupFPLayer: undefined,
      setupFPByMapLayer: undefined,
      openAreaMenu: false,
      pickerSnackbar: null
    }

    this.map = undefined
  }

  componentDidMount() {
    const {
      centerCoord,
      handleEdit,
      handleOpenDialog,
      handleDrawAction,
      onRef,
      zoom,
      handleMapClick,
      has_write_permissions,
      useCluster
    } = this.props

    onRef(this)
    this.initializeMap(centerCoord, zoom, handleEdit, handleOpenDialog, handleDrawAction, handleMapClick, has_write_permissions, useCluster)
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove()
      this.map = undefined
    }
  }

  initializeMap = (centerCoord, zoom, handleEdit, handleOpenDialog, handleDrawAction, handleMapClick, has_write_permissions, useCluster) => {
    const maxZoomTopography = 17
    const maxZoom = 20

    // Garantir que L esteja disponível globalmente (caso o plugin não tenha sido carregado ainda)
    if (typeof window !== 'undefined' && !window.L) {
      window.L = L
    }

    // Criar layers do Google Maps
    // Verificar se o plugin está disponível - pode estar em L.GridLayer.GoogleMutant ou L.gridLayer.googleMutant
    if (!L.gridLayer || !L.gridLayer.googleMutant) {
      // Tentar carregar o plugin dinamicamente se não estiver disponível
      if (typeof window !== 'undefined' && window.L && window.L.GridLayer && window.L.GridLayer.GoogleMutant) {
        // O plugin está disponível via window.L, criar a factory function se não existir
        if (!L.gridLayer.googleMutant) {
          L.gridLayer.googleMutant = function(options) {
            return new window.L.GridLayer.GoogleMutant(options)
          }
        }
      } else {
        console.error('GoogleMutant plugin not loaded. Make sure leaflet.gridlayer.googlemutant is imported correctly.')
        console.log('L.gridLayer:', L.gridLayer)
        console.log('window.L:', typeof window !== 'undefined' ? window.L : 'N/A')
        return
      }
    }

    const satellite = L.gridLayer.googleMutant({
      type: 'hybrid',
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }]
        },
      ],
      maxZoom: 20,
      minZoom: 0
    })

    const streets = L.gridLayer.googleMutant({
      type: 'roadmap',
      styles: [
        {
          featureType: 'poi',
          stylers: [{ visibility: 'off' }]
        }
      ],
      maxZoom: 20,
      minZoom: 0
    })

    const topography = L.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
      maxZoom: 17,
      type: 'topography',
      attribution: 'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (<a href="https://creativecommons.org/licenses/by-sa/3.0/">CC-BY-SA</a>)'
    })

    const baseMaps = {
      [I18n.t('v4/area.satellite')]: satellite,
      [I18n.t('v4/area.streets')]: streets,
      [I18n.t('v4/area.topography')]: topography
    }

    // Usar Google Maps como padrão
    const savedLayer = localStorage.getItem('layers')
    const defaultLayer = satellite

    const layer = baseMaps[savedLayer] || defaultLayer

    const map = L.map('dashboard-map', {
      center: centerCoord,
      zoom: layer.options.type === 'topography' ? maxZoomTopography : zoom,
      maxZoom: layer.options.type === 'topography' ? maxZoomTopography : maxZoom,
      layers: [layer],
      minZoom: 2.5,
      zoomControl: false
    })

    this.map = map

    // Ajustar z-index dos panes para garantir que desenhos fiquem acima do Google Maps
    const overlayPane = map.getPane('overlayPane')
    const tilePane = map.getPane('tilePane')

    if (overlayPane) {
      overlayPane.style.zIndex = '450'
    }
    if (tilePane) {
      tilePane.style.zIndex = '200'
    }

    L.control.layers(baseMaps, null, { position: 'bottomright' }).addTo(this.map)

    map.on('click', (e) => {
      this.openLocationPicker(e.latlng.lat, e.latlng.lng)
    })

    map.on('baselayerchange', (e) => {
      localStorage.setItem('layers', e.name)
      const current_zoom = map.getZoom()

      if (e.layer.options.type === 'topography' && current_zoom > maxZoomTopography) {
        map.setZoom(maxZoomTopography - 1)
        map.setMaxZoom(maxZoomTopography)

        setTimeout(() => {
          map.setZoom(maxZoomTopography)
        }, 1000)
      } else {
        const zoom_min = current_zoom - 1

        map.setZoom(zoom_min)
        map.setMaxZoom(maxZoom)

        setTimeout(() => {
          map.setZoom(current_zoom)
        }, 1000)
      }
    })

    const DefaultIcon = L.icon({
      iconUrl: icon,
      shadowUrl: iconShadow,
      iconSize: [25, 41],
      iconAnchor: [13, 40]
    })

    L.Marker.prototype.options.icon = DefaultIcon

    L.control.zoom({
      zoomInTitle: I18n.t('leaflet.control.zoom.zoomInTitle'),
      zoomOutTitle: I18n.t('leaflet.control.zoom.zoomOutTitle'),
      position: 'bottomright'
    }).addTo(map)

    const drawItems = new L.FeatureGroup()

    if (useCluster) {
      const clusterLayer = L.markerClusterGroup({
        removeOutsideVisibleBounds: true
      })
      const deflateLayer = L.deflate({ minSize: 30, markerLayer: clusterLayer })
      deflateLayer.addTo(map)

      this.setState({ drawItems: deflateLayer })
    } else {
      this.setState({ drawItems: drawItems })
    }

    // Adicionar layers ao mapa
    if (drawItems) {
      map.addLayer(drawItems)
      if (map.getPane('overlayPane')) {
        map.getPane('overlayPane').style.zIndex = '450'
      }
    }
    if (this.state.markersLayer) {
      map.addLayer(this.state.markersLayer)
    }

    // Garantir que o pane de markers também fique acima
    if (map.getPane('markerPane')) {
      map.getPane('markerPane').style.zIndex = '600'
    }

    // Garantir que o pane de shadow também fique acima
    if (map.getPane('shadowPane')) {
      map.getPane('shadowPane').style.zIndex = '500'
    }

    L.drawLocal = { ...L.drawLocal, ...getLeafLetTranslations() }

    const onLocationPickerClick = () => {
      const { isActivePicker, pickerSnackbar } = this.state
      const { enqueueSnackbar, closeSnackbar } = this.props

      this.setState({ isActivePicker: !isActivePicker }, () => {
        if (this.state.isActivePicker) {
          const pickerSnackbar = enqueueSnackbar(
            I18n.t('leaflet.location_picker.snackbar_msg'),
            {
              variant: 'success',
              autoHideDuration: null
            }
          )

          this.setState({ pickerSnackbar })
        } else {
          !!pickerSnackbar && closeSnackbar(pickerSnackbar)
        }

        this.changePickerStyles()
      })
    }

    const onAreaMenuClick = () => this.setState({ openAreaMenu: !this.state.openAreaMenu })

    map.addControl(
      createCustomControl({
        imageUrl: locationPickerIcone,
        title: I18n.t('leaflet.control.location_picker'),
        onClick: onLocationPickerClick,
        options: { position: 'bottomright' },
        dataSet: {
          intercomTarget: 'location-picker'
        },
        id: 'leaflet-control-location-picker'
      })
    )

    if (has_write_permissions) {
      map.addControl(
        createCustomControl({
          imageUrl: AreaMenu,
          title: 'Menu',
          onClick: onAreaMenuClick,
          options: { position: 'bottomright' },
          dataSet: {
            intercomTarget: 'area-controller-map'
          },
          id: 'leaflet-control-area-menu'
        })
      )

      const drawControl = new L.Control.Draw({
        position: 'bottomright',
        edit: {
          featureGroup: drawItems,
          remove: false
        },
        draw: {
          circlemarker: false,
          polyline: false,
          rectangle: false,
          marker: false,
          polygon: {
            allowIntersection: false,
            drawError: {
              color: '#e1e100',
              message: `<strong>${I18n.t('v4/map.intersection_error')}<strong>`
            },
          }
        }
      })

      // Garantir que o pane usado pelo leaflet-draw fique acima do Google Maps
      map.whenReady(() => {
        const overlayPane = map.getPane('overlayPane')
        const tilePane = map.getPane('tilePane')

        if (overlayPane && tilePane) {
          overlayPane.style.zIndex = '450'
          tilePane.style.zIndex = '200'
        }

        const drawPane = map.getPane('drawPane')
        if (drawPane) {
          drawPane.style.zIndex = '500'
        }
      })

      this.setState({ drawControl: drawControl })
      map.addControl(drawControl)
    }

    map.on(L.Draw.Event.CREATED, (e) => {
      const layerType = e.layerType
      const layer = e.layer
      let size = 0

      layer.type = layerType
      drawItems.addLayer(layer)

      if (layerType === 'polygon') {
        const areaPol = L.GeometryUtil.geodesicArea(layer.getLatLngs()[0])
        layer.center = layer.getCenter()
        try {
          size = L.GeometryUtil.readableArea(areaPol, true)
        } catch (error) {
          console.warn('Error calculating polygon area:', error)
          size = (areaPol / 10000).toFixed(2) + ' ha'
        }
      }

      if (layerType === 'circle') {
        layer.coordinates = circleToPolygon(L, layer, 36, this.map)
        const radius = layer._mRadius
        const areaInSquareMeters = 3.14 * (radius * radius)
        try {
          size = L.GeometryUtil.readableArea(areaInSquareMeters, true)
        } catch (error) {
          console.warn('Error calculating circle area:', error)
          size = (areaInSquareMeters / 10000).toFixed(2) + ' ha'
        }
      }

      layer.size = size
      handleOpenDialog(layer, drawItems)
    })

    map.on('draw:edited', (e) => {
      const layers = e.layers
      let size_ha = 0
      let radius

      layers.eachLayer(async layer => {
        const { id } = layer.options.info
        let center
        let coordinates

        if (layer.options.info.kind === 'polygon') {
          coordinates = layer.getLatLngs()[0]
          center = layer.getCenter()

          const newAreaPol = L.GeometryUtil.geodesicArea(layer.getLatLngs()[0])
          try {
            size_ha = L.GeometryUtil.readableArea(newAreaPol, true)
          } catch (error) {
            console.warn('Error calculating polygon area:', error)
            size_ha = (newAreaPol / 10000).toFixed(2) + ' ha'
          }
        }

        if (layer.options.info.kind === 'circle') {
          coordinates = circleToPolygon(L, layer, 36, this.map)
          center = layer._latlng
          radius = layer._mRadius
          const areaInSquareMeters = 3.14 * (radius * radius)
          try {
            size_ha = L.GeometryUtil.readableArea(areaInSquareMeters, true)
          } catch (error) {
            console.warn('Error calculating circle area:', error)
            size_ha = (areaInSquareMeters / 10000).toFixed(2) + ' ha'
          }
        }

        await handleEdit({
          id,
          size_ha,
          radius,
          latitude: center.lat,
          longitude: center.lng,
          coordinates
        })
      })
    })

    map.on('draw:editstop', () => {
      handleDrawAction('editing', false)
    })

    map.on('draw:editstart', () => {
      handleDrawAction('editing', true)
    })

    map.on('draw:drawstop', () => {
      handleDrawAction('drawing', false)
    })

    map.on('draw:drawstart', () => {
      handleDrawAction('drawing', true)
    })

    const mapBounds = L.latLngBounds([
      [-90, -180],
      [90, 180]
    ]);
    map.setMaxBounds(mapBounds);
    map.on('click', handleMapClick)
    map.invalidateSize()
  }

  changeMapCenter = (center, areaSize) => {
    this.map.flyTo(center, getZoom(areaSize))
  }

  cleanGeocoderLayer = () => {
    const { geocoderLayer } = this.state

    if (geocoderLayer) {
      this.map.removeLayer(geocoderLayer)
    }
  }

  addSearchInfoInMap = (coordinates, info, hasPin) => {
    this.cleanGeocoderLayer()

    const layer = !!hasPin
      ? new L.marker(coordinates).addTo(this.map)
        .bindPopup(info)
        .openPopup()
      : new L.popup()
        .setLatLng(coordinates)
        .setContent(info)
        .openOn(this.map)

    this.setState({ geocoderLayer: layer })
  }

  cleanLayer = (layer) => {
    if (!!this.state[layer]) {
      this.map.removeLayer(this.state[layer])
    }
  }

  addNewLayerPin = ({ lat, lng }) => {
    const { menusStatus: { menus, toggleMenu, setMenuData } } = this.props
    const { data, step } = menus[MENUS.weatherForecastProduct]
    const { open } = menus[MENUS.weatherForecastSetupFixedModal]

    this.cleanLayer('setupFPByMapLayer')

    if (open && (step === 1 || step === 2)) {
      const layer = new L.marker([lat, lng]).addTo(this.map)
      this.setState({ setupFPByMapLayer: layer })

      step === 1 && toggleMenu(MENUS.weatherForecastSetupLatLongModal, true)
      setMenuData(MENUS.weatherForecastProduct, 'data', { ...data, lat, lon: lng })
    }
  }

  addPin = (coordinates) => {
    const { menusStatus: { toggleMenu } } = this.props

    this.cleanLayer('setupFPLayer')
    const layer = new L.marker(coordinates).addTo(this.map)

    this.changeMapCenter(coordinates)
    this.map.once('zoomend', () => {
      toggleMenu(MENUS.weatherForecastSetupLatLongModal, true)
    })

    this.setState({ setupFPLayer: layer })
  }

  applyFilter = (imageUrl, area) => {
    this.removeCurrentFilter()

    const filter = L.imageOverlay(imageUrl, getImageBounds(area))

    this.setState({ filter, currentAreaWithFilter: area }, () => {
      filter.addTo(this.map)
    })
  }

  removeCurrentFilter = () => {
    const { filter } = this.state

    if (filter) {
      this.setState({ filter: null, currentAreaWithFilter: undefined }, () =>
        this.map.removeLayer(filter)
      )
    }
  }

  getCenterOfView = () => this.map.getCenter()

  getAreaGroup = () => {
    const { alerts, mapAreas = [] } = this.props
    let alertsOnTheMap = []

    if (!_.isEmpty(mapAreas) && !_.isEmpty(alerts)) {
      const newAlerts = alerts.map(alert => {
        const { areas, ...noAreas } = alert
        const currentAlerts = areas.map(area => ({ ...area, ...noAreas }))

        return currentAlerts
      }).flat()

      const areaGroup = _.groupBy(newAlerts, 'id')

      alertsOnTheMap = Object.keys(areaGroup).map(area => {
        const currentArea = areaGroup[area][0].id

        const newArea = mapAreas.find(mapArea => mapArea.id === currentArea)

        return {
          areaName: newArea.name,
          latitude: newArea.latitude,
          longitude: newArea.longitude,
          alerts: areaGroup[area]
        }
      })
    }

    return alertsOnTheMap
  }

  openLocationPicker = (latitude, longitude) => {
    const { isActivePicker, pickerSnackbar } = this.state
    const { classes, closeSnackbar } = this.props

    if (isActivePicker) {
      let locationPickerPopup = L.popup({
        minWidth: 94
      })

      locationPickerPopup
        .setLatLng([latitude, longitude])
        .setContent(locationPickerPopupContent(latitude, longitude, classes))
        .openOn(this.map)

      this.setState({ isActivePicker: false },
        () => {
          this.changePickerStyles()
          closeSnackbar(pickerSnackbar)
        }
      )
    }
  }

  enableOrDisableArea = () => {
    const { areaMenuData } = this.props

    const newLayers = Object.keys(this.map._layers)
      .map(layer => this.map._layers[layer])
      .filter(isLayer => !_.isEmpty(isLayer.editing))

    return newLayers.find(layer => layer.options.info.id === areaMenuData.id)
  }

  changePickerStyles = () => {
    const { isActivePicker } = this.state

    const icon = isActivePicker ? locationPickerActiveIcone : locationPickerIcone
    const color = isActivePicker ? '#66A343' : '#FFF'
    changeCustomControl(icon, color, 'leaflet-control-location-picker')
  }

  handleCloseOrOpenEditAreaMenu = (closeOrOpen) => {
    const { menusStatus: { toggleMenu } } = this.props

    toggleMenu(MENUS.mapEditAreaMenu, closeOrOpen)
  }

  handleCancelAreaMenu = () => {
    this.enableOrDisableArea().editing.disable()

    this.handleCloseOrOpenEditAreaMenu(false)
  }

  handleSaveAreaMenu = () => {
    const { enqueueSnackbar } = this.props

    this.handleCloseOrOpenEditAreaMenu(false)

    enqueueSnackbar(I18n.t('v4/map.edit_area_menu.snackbar'), { variant: 'success' })

    return new L['EditToolbar'].Edit(this.map, {
      featureGroup: this.state.drawControl.options.edit.featureGroup,
      selectedPathOptions: this.state.drawControl.options.edit.selectedPathOptions
    }).save()
  }

  render() {
    const { drawItems, markersLayer, currentAreaWithFilter, openAreaMenu } = this.state

    const {
      circleAreas,
      classes,
      editing,
      forecastPointsList,
      handleAreaClick,
      polygonAreas,
      markers,
      menusStatus: { menus, setMenuData, toggleMenu },
      handleAlertSelect,
      handleEquipmentClick,
      changeAreasColors,
      mapAreas,
      removeColorsFromAreas,
      hasForecastProduct,
      handleAreaFileControlClick,
      has_write_permissions,
      equipment
    } = this.props

    const isOpenModal = menus[MENUS.weatherForecastSetupFixedModal].open
    const isOpenConfirmModal = menus[MENUS.weatherForecastSetupLatLongModal].open
    const isOpenMapSatelliteImagery =
      menus[MENUS.mapSatelliteImagery].open
      || menus[MENUS.mapSatelliteImageryScale].open
      || menus[MENUS.irrigationProduct].open

    !!this.map && changeMapStyle(isOpenMapSatelliteImagery)

    !isOpenMapSatelliteImagery && this.removeCurrentFilter()

    if (has_write_permissions) {
      !!this.map && changeAreaMenuStyle(openAreaMenu)
    }

    !isOpenModal && !isOpenConfirmModal && this.cleanLayer('setupFPByMapLayer')

    if (!this.map || !drawItems || !markersLayer) {
      return (
        <div id='dashboard-map' className={classes.map}>
          <ProductsFloatingMenu
            addPin={this.addPin}
            cleanLayer={this.cleanLayer}
            mapAreas={mapAreas}
            changeMapCenter={this.changeMapCenter}
            changeAreasColors={changeAreasColors}
            removeColorsFromAreas={removeColorsFromAreas}
            hasForecastProduct={hasForecastProduct}
          />
          {openAreaMenu &&
            <CustomMenu
              handleAreaFileControlClick={handleAreaFileControlClick}
              open={openAreaMenu}
              onClose={() => this.setState({ openAreaMenu: false })}
            />
          }
        </div>
      )
    }

    drawItems.clearLayers()
    markersLayer.clearLayers()
    circleAreas.map(area => {
      areNotEqual(area, currentAreaWithFilter) && !area.hide &&
        L.circle([area.latitude, area.longitude], {
          color: area.color || defaultAreaColor,
          radius: area.radius,
          size_ha: area.size_ha,
          info: { kind: area.kind, id: area.id }
        })
          .addTo(drawItems)
          .on('click', () => {
            if (!editing && !isOpenModal) {
              handleAreaClick(area.id)
            }
          })
    })

    polygonAreas.map(area => {
      areNotEqual(area, currentAreaWithFilter) && !area.hide &&
        new L.polygon(area.coordinates, {
          color: area.color || defaultAreaColor,
          size_ha: area.size_ha,
          info: { kind: area.kind, id: area.id }
        })
          .addTo(drawItems)
          .on('click', () => {
            if (!editing && !isOpenModal) {
              handleAreaClick(area.id)
            }
          })
    })

   markers.map(marker => {
      L.marker([
        marker.latitude, marker.longitude
      ],
        {
          icon: L.icon({
            iconUrl: getEquipmentIcon(marker),
            iconSize: [50, 50]
          })
        }
      )
        .addTo(markersLayer)
        .on('click', () => {
          if (!editing && !isOpenModal) {
            handleEquipmentClick(marker)
            this.openLocationPicker(marker.latitude, marker.longitude)
          }
        })
    })

    this.getAreaGroup().map(marker => {
      L.marker([
        marker.latitude, marker.longitude
      ],
        {
          icon: L.icon({
            iconUrl: Alert,
            iconSize: [25, 41]
          })
        }
      )
        .addTo(markersLayer)
        .on('click', () => {
          if (!editing && !isOpenModal) {
            handleAlertSelect(marker)
            this.openLocationPicker(marker.latitude, marker.longitude)
          }
        })
    })

    forecastPointsList.map(forecastPoint => {
      L.marker([
        forecastPoint.lat, forecastPoint.lon
      ],
        {
          icon: L.icon({
            iconUrl: WeatherForecast,
            iconSize: [50, 50]
          })
        }
      )
        .addTo(markersLayer)
        .on('click', () => {
          if (!editing && !isOpenModal) {
            setMenuData(MENUS.weatherForecastProduct, 'forecastPoint', forecastPoint)
            toggleMenu(MENUS.weatherForecastProduct, true)
            this.openLocationPicker(forecastPoint.lat, forecastPoint.lon)
            sendAmplitudeEvent('Map - Clicked weather forecast icon', {
              id_product: 1
            })
            IntercomAPI('trackEvent', 'Map - Clicked weather forecast icon')
          }
        })
    })

    return (
      <div id='dashboard-map' className={classes.map}>
        <ProductsFloatingMenu
          addPin={this.addPin}
          cleanLayer={this.cleanLayer}
          mapAreas={mapAreas}
          changeMapCenter={this.changeMapCenter}
          changeAreasColors={changeAreasColors}
          removeColorsFromAreas={removeColorsFromAreas}
          hasForecastProduct={hasForecastProduct}
        />

        {
          openAreaMenu &&
          <CustomMenu
            handleAreaFileControlClick={handleAreaFileControlClick}
            open={openAreaMenu}
            onClose={() => this.setState({ openAreaMenu: false })}
          />
        }
      </div>
    )
  }
}

Map.defaultProps = {
  onRef: () => { },
}

export default
  withMenusStatus(
    withSnackbar(
      withStyles(styles)(Map)
    )
  )
